import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Divider,
  Grid,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  PictureAsPdf as PdfIcon,
  Save as SaveIcon,
  ContentCopy as CopyIcon,
  Close as CloseIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
} from '@mui/icons-material';
import { documentsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const DISCOUNT_TYPES = [
  { value: 'none', label: 'Aucune' },
  { value: 'percentage', label: 'Pourcentage (%)' },
  { value: 'fixed', label: 'Montant fixe (DZD)' },
];

const LINE_TYPES = [
  { value: 'installation', label: 'Frais d\'installation' },
  { value: 'subscription', label: 'Abonnement' },
  { value: 'service', label: 'Service' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'equipment', label: 'Équipement' },
  { value: 'product', label: 'Produit' },
  { value: 'discount', label: 'Remise' },
  { value: 'note', label: 'Note' },
  { value: 'other', label: 'Autre' },
];

function DocumentEditModal({ open, onClose, document, onSuccess }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const [documentData, setDocumentData] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [globalDiscount, setGlobalDiscount] = useState({
    type: 'none',
    value: 0,
  });
  const [adminEditPin, setAdminEditPin] = useState('');
  const [globalVatRate, setGlobalVatRate] = useState(19);
  const [pdfUrl, setPdfUrl] = useState(null);


  // Load document details
  useEffect(() => {
    if (open && document?.id) {
      loadDocumentDetails();
    }
  }, [open, document?.id, loadDocumentDetails]);

  const loadDocumentDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await documentsAPI.get(document.id);
      const data = response.data;
      setDocumentData(data);
      
      // Set line items
      if (data.invoice_items && data.invoice_items.length > 0) {
        setLineItems(data.invoice_items.map(item => ({
          id: item.id,
          description: item.description || '',
          line_type: item.line_type || 'subscription',
          quantity: item.quantity || 1,
          months: item.months || 1,
          unit_price_ht: parseFloat(item.unit_price_ht) || 0,
          discount_type: item.discount_type || 'none',
          discount_value: parseFloat(item.discount_value) || 0,
          vat_rate: parseFloat(item.vat_rate) || 0,
        })));
      }
      
      // Set global discount
      if (data.invoice_details) {
        setGlobalDiscount({
          type: data.invoice_details.global_discount_type || 'none',
          value: parseFloat(data.invoice_details.global_discount_value) || 0,
        });
        setGlobalVatRate(parseFloat(data.invoice_details.global_vat_rate) || 19);
      }
      
      // Set PDF URL
      if (data.pdf_file) {
        setPdfUrl(data.pdf_file);
      }
    } catch (err) {
      console.error('Error loading document:', err);
      setError('Erreur lors du chargement du document');
    } finally {
      setLoading(false);
    }
  }, [document?.id]);

  // Calculate totals
  const calculateTotals = useCallback(() => {
    let subtotalHT = 0;
    
    lineItems.forEach(item => {
      // Skip note lines from calculation
      if (item.line_type === 'note') return;
      
      const lineTotal = (item.quantity || 1) * (item.months || 1) * (item.unit_price_ht || 0);
      subtotalHT += lineTotal;
    });
    
    // Apply global discount
    let discountAmount = 0;
    if (globalDiscount.type === 'percentage' && globalDiscount.value > 0) {
      discountAmount = subtotalHT * (globalDiscount.value / 100);
    } else if (globalDiscount.type === 'fixed' && globalDiscount.value > 0) {
      discountAmount = Math.min(globalDiscount.value, subtotalHT);
    }
    
    const totalHT = subtotalHT - discountAmount;
    const totalVAT = totalHT * (globalVatRate / 100);
    const totalTTC = totalHT + totalVAT;
    
    return { subtotalHT, discountAmount, totalHT, totalVAT, totalTTC };
  }, [lineItems, globalDiscount, globalVatRate]);

  // Add new line item
  const addLineItem = () => {
    setLineItems([...lineItems, {
      description: '',
      line_type: 'subscription',
      quantity: 1,
      months: 1,
      unit_price_ht: 0,
      discount_type: 'none',
      discount_value: 0,
      vat_rate: 0,
    }]);
  };

  // Update line item
  const updateLineItem = (index, field, value) => {
    setLineItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Remove line item
  const removeLineItem = (index) => {
    setLineItems(prev => prev.filter((_, i) => i !== index));
  };

  // Move line item up
  const moveUp = (index) => {
    if (index === 0) return; // Can't move up if already at top
    setLineItems(prev => {
      const newItems = [...prev];
      [newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]];
      return newItems;
    });
  };

  // Move line item down
  const moveDown = (index) => {
    if (index === lineItems.length - 1) return; // Can't move down if already at bottom
    setLineItems(prev => {
      const newItems = [...prev];
      [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
      return newItems;
    });
  };

  // Save changes
  const handleSave = async () => {
    if (lineItems.length === 0) {
      setError('Veuillez ajouter au moins une ligne');
      return;
    }
    
    // Check if at least one item has valid data
    const validItems = lineItems.filter(item => item.description && item.unit_price_ht > 0);
    if (validItems.length === 0) {
      setError('Veuillez remplir au moins une ligne correctement');
      return;
    }
    
    // Check PIN for validated invoices
    if (documentData?.status === 'validated' && documentData?.document_type === 'invoice' && user?.is_staff) {
      if (!adminEditPin.trim()) {
        setError('Le code PIN administrateur est requis pour modifier une facture validée');
        return;
      }
    }
    
    try {
      setSaving(true);
      setError(null);
      
      // Send all line items, not just valid ones, to preserve user's work
      const payload = {
        line_items: lineItems.map((item, index) => ({
          id: item.id || undefined,  // Include ID if it exists, otherwise undefined for new items
          description: item.description,
          line_type: item.line_type,
          quantity: parseInt(item.quantity) || 1,
          months: parseInt(item.months) || 1,
          unit_price_ht: parseFloat(item.unit_price_ht) || 0,
          discount_type: item.discount_type || 'none',
          discount_value: parseFloat(item.discount_value) || 0,
          vat_rate: 0, // VAT is global
        })),
        global_discount_type: globalDiscount.type,
        global_discount_value: parseFloat(globalDiscount.value) || 0,
      };
      
      // Include PIN for validated invoices
      if (documentData?.status === 'validated' && documentData?.document_type === 'invoice' && user?.is_staff) {
        payload.admin_edit_pin = adminEditPin.trim();
      }
      
      await documentsAPI.updateItems(document.id, payload);
      setSuccess('Document mis à jour avec succès');
      
      // Reload document
      await loadDocumentDetails();
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving document:', err);
      setError(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // Generate/Regenerate PDF
  const handleGeneratePDF = async () => {
    try {
      setGenerating(true);
      setError(null);
      
      const response = await documentsAPI.generatePDF(document.id);
      
      if (response.data.status === 'success') {
        setPdfUrl(response.data.pdf_url);
        setSuccess('PDF généré avec succès');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(response.data.message || 'Erreur lors de la génération');
      }
    } catch (err) {
      console.error('Error generating PDF:', err);
      setError(err.response?.data?.message || 'Erreur lors de la génération du PDF');
    } finally {
      setGenerating(false);
    }
  };

  // Duplicate document (for rejected invoices)
  const handleDuplicate = async () => {
    try {
      setDuplicating(true);
      setError(null);
      
      const response = await documentsAPI.duplicate(document.id);
      
      if (response.data.status === 'success') {
        setSuccess(`Document dupliqué: ${response.data.document.document_number}`);
        setTimeout(() => {
          handleClose();
        }, 1500);
      } else {
        setError(response.data.error || 'Erreur lors de la duplication');
      }
    } catch (err) {
      console.error('Error duplicating document:', err);
      setError(err.response?.data?.error || 'Erreur lors de la duplication');
    } finally {
      setDuplicating(false);
    }
  };

  const handleClose = () => {
    setDocumentData(null);
    setLineItems([]);
    setGlobalDiscount({ type: 'none', value: 0 });
    setPdfUrl(null);
    setError(null);
    setSuccess(null);
    onClose();
    if (onSuccess) onSuccess();
  };

  const totals = calculateTotals();
  const isEditable = documentData?.status === 'draft' || (documentData?.status === 'validated' && documentData?.document_type === 'invoice' && user?.is_staff);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6">
              {documentData?.document_number || 'Document'}
            </Typography>
            {documentData && (
              <Chip
                label={documentData.document_type === 'proforma' ? 'Proforma' : 
                       documentData.document_type === 'invoice' ? 'Facture' : 'Attestation'}
                color={documentData.document_type === 'proforma' ? 'warning' : 
                       documentData.document_type === 'invoice' ? 'success' : 'primary'}
                size="small"
              />
            )}
            {!isEditable && (
              <Chip label="Non modifiable" color="error" size="small" variant="outlined" />
            )}
          </Box>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
            
            {/* Show duplicate option for rejected documents */}
            {documentData?.status === 'rejected' && (
              <Alert 
                severity="warning" 
                sx={{ mb: 2 }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    startIcon={duplicating ? <CircularProgress size={16} /> : <CopyIcon />}
                    onClick={handleDuplicate}
                    disabled={duplicating}
                  >
                    Dupliquer et modifier
                  </Button>
                }
              >
                Ce document a été rejeté. Vous pouvez le dupliquer pour créer une nouvelle version modifiable.
              </Alert>
            )}
            
            <Box>
                {/* Client Info */}
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" color="text.secondary">Client</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {documentData?.client_details?.company_name || documentData?.client_name || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" color="text.secondary">Date</Typography>
                      <Typography variant="body1">
                        {documentData?.document_date ? new Date(documentData.document_date).toLocaleDateString('fr-FR') : '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" color="text.secondary">Statut</Typography>
                      <Typography variant="body1">
                        {documentData?.status === 'draft' ? 'Brouillon' : 
                         documentData?.status === 'pending_client' ? 'Envoyé' : 
                         documentData?.status === 'validated' ? 'Validé' : documentData?.status}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
                
                {/* Admin PIN for validated invoices */}
                {documentData?.status === 'validated' && documentData?.document_type === 'invoice' && user?.is_staff && (
                  <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'warning.light' }}>
                    <Typography variant="subtitle2" gutterBottom color="warning.dark">
                      Code PIN Administrateur Requis
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Pour modifier une facture définitive validée, veuillez saisir le code PIN administrateur.
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="password"
                      label="Code PIN Administrateur"
                      value={adminEditPin}
                      onChange={(e) => setAdminEditPin(e.target.value)}
                      placeholder="Entrez le code PIN"
                      required
                      sx={{ maxWidth: 300 }}
                    />
                  </Paper>
                )}
                
                {/* Line Items */}
                {documentData?.document_type !== 'attestation' && (
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6">Lignes de facturation</Typography>
                      {isEditable && (
                        <Button startIcon={<AddIcon />} onClick={addLineItem} variant="outlined" size="small">
                          Ajouter une ligne
                        </Button>
                      )}
                    </Box>
                    
                    <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell width="30%"><strong>Description</strong></TableCell>
                            <TableCell width="12%"><strong>Type</strong></TableCell>
                            <TableCell width="8%" align="center"><strong>Qté</strong></TableCell>
                            <TableCell width="8%" align="center"><strong>Mois</strong></TableCell>
                            <TableCell width="12%" align="right"><strong>P.U. HT</strong></TableCell>
                            <TableCell width="15%" align="right"><strong>Total HT</strong></TableCell>
                            <TableCell width="15%" align="center"><strong>Actions</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {lineItems.map((item, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                {isEditable ? (
                                  <TextField
                                    fullWidth
                                    size="small"
                                    value={item.description}
                                    onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                                    placeholder="Description"
                                  />
                                ) : item.description}
                              </TableCell>
                              <TableCell>
                                {isEditable ? (
                                  <Select
                                    fullWidth
                                    size="small"
                                    value={item.line_type}
                                    onChange={(e) => updateLineItem(index, 'line_type', e.target.value)}
                                  >
                                    {LINE_TYPES.map(t => (
                                      <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                                    ))}
                                  </Select>
                                ) : LINE_TYPES.find(t => t.value === item.line_type)?.label || item.line_type}
                              </TableCell>
                              <TableCell align="center">
                                {isEditable ? (
                                  item.line_type === 'note' ? (
                                    <Typography sx={{ textAlign: 'center', color: 'text.secondary' }}>--</Typography>
                                  ) : (
                                    <TextField
                                      size="small"
                                      type="number"
                                      value={item.quantity}
                                      onChange={(e) => updateLineItem(index, 'quantity', parseInt(e.target.value) || 1)}
                                      inputProps={{ min: 1, style: { textAlign: 'center' } }}
                                      sx={{ width: 60 }}
                                    />
                                  )
                                ) : item.line_type === 'note' ? '--' : item.quantity}
                              </TableCell>
                              <TableCell align="center">
                                {isEditable ? (
                                  item.line_type === 'note' ? (
                                    <Typography sx={{ textAlign: 'center', color: 'text.secondary' }}>--</Typography>
                                  ) : (
                                    <TextField
                                      size="small"
                                      type="number"
                                      value={item.months}
                                      onChange={(e) => updateLineItem(index, 'months', parseInt(e.target.value) || 1)}
                                      inputProps={{ min: 1, style: { textAlign: 'center' } }}
                                      sx={{ width: 60 }}
                                    />
                                  )
                                ) : item.line_type === 'note' ? '--' : item.months}
                              </TableCell>
                              <TableCell align="right">
                                {isEditable ? (
                                  item.line_type === 'note' ? (
                                    <Typography sx={{ textAlign: 'right', color: 'text.secondary' }}>--</Typography>
                                  ) : (
                                    <TextField
                                      size="small"
                                      type="number"
                                      value={item.unit_price_ht}
                                      onChange={(e) => updateLineItem(index, 'unit_price_ht', parseFloat(e.target.value) || 0)}
                                      inputProps={{ min: 0, step: 100, style: { textAlign: 'right' } }}
                                      sx={{ width: 100 }}
                                    />
                                  )
                                ) : item.line_type === 'note' ? '--' : parseFloat(item.unit_price_ht).toLocaleString('fr-FR')}
                              </TableCell>
                              <TableCell align="right">
                                <strong>
                                  {item.line_type === 'note' ? (
                                    <Typography sx={{ color: 'text.secondary' }}>-- DZD</Typography>
                                  ) : (
                                    <span>
                                      {((item.quantity || 1) * (item.months || 1) * (item.unit_price_ht || 0)).toLocaleString('fr-FR')} DZD
                                    </span>
                                  )}
                                </strong>
                              </TableCell>
                              {isEditable && (
                                <TableCell align="center">
                                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                    <IconButton 
                                      size="small" 
                                      onClick={() => moveUp(index)}
                                      disabled={index === 0}
                                    >
                                      <ArrowUpIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton 
                                      size="small" 
                                      onClick={() => moveDown(index)}
                                      disabled={index === lineItems.length - 1}
                                    >
                                      <ArrowDownIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton size="small" color="error" onClick={() => removeLineItem(index)}>
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </TableCell>
                              )}
                            </TableRow>
                          ))}
                          {lineItems.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={isEditable ? 7 : 6} align="center">
                                <Typography color="text.secondary">Aucune ligne</Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    
                    {/* Discount & Totals */}
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>Remise Globale</Typography>
                          <Box sx={{ display: 'flex', gap: 2 }}>
                            <FormControl size="small" sx={{ minWidth: 150 }}>
                              <InputLabel>Type</InputLabel>
                              <Select
                                value={globalDiscount.type}
                                label="Type"
                                onChange={(e) => setGlobalDiscount(prev => ({ ...prev, type: e.target.value }))}
                                disabled={!isEditable}
                              >
                                {DISCOUNT_TYPES.map(t => (
                                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            {globalDiscount.type !== 'none' && (
                              <TextField
                                size="small"
                                type="number"
                                label={globalDiscount.type === 'percentage' ? 'Pourcentage' : 'Montant'}
                                value={globalDiscount.value}
                                onChange={(e) => setGlobalDiscount(prev => ({ ...prev, value: parseFloat(e.target.value) || 0 }))}
                                disabled={!isEditable}
                                inputProps={{ min: 0 }}
                                sx={{ width: 120 }}
                              />
                            )}
                          </Box>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography>Sous-total HT:</Typography>
                            <Typography>{totals.subtotalHT.toLocaleString('fr-FR')} DZD</Typography>
                          </Box>
                          {totals.discountAmount > 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, color: 'error.main' }}>
                              <Typography>Remise:</Typography>
                              <Typography>-{totals.discountAmount.toLocaleString('fr-FR')} DZD</Typography>
                            </Box>
                          )}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography>Total HT:</Typography>
                            <Typography>{totals.totalHT.toLocaleString('fr-FR')} DZD</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography>TVA ({globalVatRate}%):</Typography>
                            <Typography>{totals.totalVAT.toLocaleString('fr-FR')} DZD</Typography>
                          </Box>
                          <Divider sx={{ my: 1 }} />
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="h6">Total TTC:</Typography>
                            <Typography variant="h6" color="primary">
                              {totals.totalTTC.toLocaleString('fr-FR')} DZD
                            </Typography>
                          </Box>
                        </Paper>
                      </Grid>
                    </Grid>
                  </>
                )}
                
                {/* Attestation vehicles */}
                {documentData?.document_type === 'attestation' && documentData?.attestation_vehicles && (
                  <>
                    <Typography variant="h6" gutterBottom>Véhicules</Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>N°</strong></TableCell>
                            <TableCell><strong>Immatriculation</strong></TableCell>
                            <TableCell><strong>IMEI GPS</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {documentData.attestation_vehicles.map((av, index) => (
                            <TableRow key={index}>
                              <TableCell>{av.line_number}</TableCell>
                              <TableCell>{av.vehicle_registration || '-'}</TableCell>
                              <TableCell>{av.device_imei || '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </Box>
          </>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          startIcon={generating ? <CircularProgress size={18} /> : <PdfIcon />}
          onClick={handleGeneratePDF}
          disabled={generating || loading}
          variant="outlined"
        >
          {pdfUrl ? 'Régénérer PDF' : 'Générer PDF'}
        </Button>
        
        {isEditable && documentData?.document_type !== 'attestation' && (
          <Button
            startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving || loading}
            variant="contained"
            color="primary"
          >
            Enregistrer
          </Button>
        )}
        
        <Button onClick={handleClose}>Fermer</Button>
      </DialogActions>

    </Dialog>
  );
}

export default DocumentEditModal;
